/**
 * P3-2 公海池 /customers/pool
 *
 * 销售员 + 主管 + admin 都可访问；显示当前 available 客户，可"抢单"。
 * 抢单后 3 个月不能再被同 user 丢回。
 */
import { useState } from 'react';
import { Modal, Input, message } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { fmtRelative } from '../../lib/format';
import {
  PageHeader, HomeCard, Btn, Pill, TableWrap, healthLevel,
} from '../../components/ui';

export function CustomerPoolV2Page() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [picked, setPicked] = useState<any | null>(null);
  const [reason, setReason] = useState('');

  const { data: pool } = useQuery<any[]>({
    queryKey: ['customer-pool-v2'],
    queryFn: () => $.get('/customer-pool-v2/available'),
    refetchInterval: 30_000,
  });

  const claim = useMutation({
    mutationFn: () => $.post(`/customer-pool-v2/${picked.customer_id}/claim`, { reason }),
    onSuccess: (r: any) => {
      message.success('🎉 抢单成功！3 个月保护期内你不会被同步丢回公海');
      qc.invalidateQueries({ queryKey: ['customer-pool-v2'] });
      setPicked(null); setReason('');
      // 跳到客户详情
      nav(`/customers/${picked.customer_id}`);
    },
    onError: (e: any) => {
      // 问题2：抢一个已被他人抢走的客户 → 后端 409「该客户已被他人抢走」(message 已透传)。
      //   刷新公海让该客户从列表消失、关弹窗；api 拦截器对此类错误已弹过一次(e.handled)，避免重复 toast。
      qc.invalidateQueries({ queryKey: ['customer-pool-v2'] });
      setPicked(null); setReason('');
      if (!e?.handled) message.error(e?.message ?? '抢单失败');
    },
  });

  return (
    <>
      <PageHeader
        breadcrumb={['客户', '公海池']}
        title="🌊 公海池"
        subtitle={`当前可抢 ${pool?.length ?? 0} 个客户 · 抢单成交业绩双倍归你`}
      />
      <div className="work-content">
        <HomeCard title="可抢客户">
          <TableWrap>
            <table className="ck-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>客户</th>
                  <th>阶段</th>
                  <th>原负责人</th>
                  <th>进公海时间</th>
                  <th>原因</th>
                  <th>健康度</th>
                  <th>上次跟进</th>
                  <th style={{ textAlign: 'right' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {(pool ?? []).length === 0 && (
                  <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: 'var(--text-3)' }}>
                    🎉 公海池暂无客户
                  </td></tr>
                )}
                {(pool ?? []).map((p: any) => (
                  <tr key={p.id}>
                    <td>
                      <Link to={`/customers/${p.customer_id}`} style={{ fontWeight: 500 }}>
                        {p.nickname}
                      </Link>
                    </td>
                    <td><Pill tone="default">{p.current_stage}</Pill></td>
                    <td>{p.original_owner_name ?? '—'}</td>
                    <td>{fmtRelative(p.entered_pool_at)}</td>
                    <td>
                      <Pill tone={p.entered_reason === 'auto_force' ? 'danger' : 'warning'}>
                        {p.entered_reason === 'auto_force' ? '系统回收' : '主动放公海'}
                      </Pill>
                    </td>
                    <td>
                      <span style={{
                        color: { green: 'var(--success)', yellow: 'var(--warning)', red: 'var(--danger)' }[healthLevel(p.followup_health_score)],
                      }}>
                        {p.followup_health_score ?? '—'}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-3)' }}>
                      {p.last_followup_at ? fmtRelative(p.last_followup_at) : '从未'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Btn kind="primary" onClick={() => setPicked(p)}>抢单</Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </HomeCard>
      </div>

      <Modal
        title={picked ? `抢单 · ${picked.nickname}` : ''}
        open={!!picked}
        onCancel={() => { setPicked(null); setReason(''); }}
        footer={
          <>
            <Btn kind="default" onClick={() => { setPicked(null); setReason(''); }}>取消</Btn>
            <Btn kind="primary" onClick={() => claim.mutate()} disabled={claim.isPending}>
              {claim.isPending ? '处理中…' : '确认抢单'}
            </Btn>
          </>
        }
      >
        <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--text-2)' }}>
          抢单成功后客户立刻转到你名下。<br />
          <strong>3 个月保护期</strong>内你将不能再被同步丢回公海。<br />
          复活客户成交时业绩翻倍计算。
        </div>
        <div>
          <div style={{ fontSize: 12, marginBottom: 4 }}>抢单理由（可选）</div>
          <Input.TextArea value={reason} onChange={(e) => setReason(e.target.value)}
                          rows={3} placeholder="例如：宝妈客户我做过 50+ 成单" />
        </div>
      </Modal>
    </>
  );
}
